import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/use-toast";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import { useResendShippingDeliveryMutation } from "@/services/conciliation-dashboard";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { DASHBOARD_LABELS, DASHBOARD_TOASTS } from "../messages";
import { ShippingRecipient } from "../types";

interface ResendModalProps {
  kindLabel: string;
  recipient: ShippingRecipient;
}

export function ResendModal({
  kindLabel,
  recipient,
}: ResendModalProps): JSX.Element {
  const { handleModal } = useDialogModal();
  const [reason, setReason] = useState<string>("");
  const [resend, { isLoading }] = useResendShippingDeliveryMutation();

  const handleResend = (): void => {
    void (async (): Promise<void> => {
      try {
        await resend({ id: recipient.id, reason }).unwrap();
        toast(DASHBOARD_TOASTS.RESENT());
        handleModal({ isOpen: false });
      } catch (error: unknown) {
        toast(
          DASHBOARD_TOASTS.RESEND_FAILED(
            extractRtkErrorMessage(error, DASHBOARD_LABELS.RESEND_FALLBACK)
          )
        );
      }
    })();
  };

  return (
    <div className="w-full max-w-md space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
          <RefreshCw className="h-5 w-5 text-blue-600" />
        </div>
        <p className="font-medium text-gray-900">
          {DASHBOARD_LABELS.RESEND_TITLE(kindLabel)}
        </p>
      </div>

      <div className="space-y-1 rounded-md bg-blue-50 p-3 text-sm">
        <p className="font-medium text-blue-900">{recipient.name}</p>
        <p className="text-xs text-blue-800">{recipient.email}</p>
        {recipient.failed_at && (
          <p className="text-xs text-blue-800">
            {DASHBOARD_LABELS.LAST_FAILURE_AT(
              new Date(recipient.failed_at).toLocaleString("pt-BR")
            )}
          </p>
        )}
        <p className="text-xs text-blue-700">
          {DASHBOARD_LABELS.RESEND_AUDIT_NOTE}
        </p>
      </div>

      <div className="space-y-1">
        <label htmlFor="resend-reason" className="text-sm text-gray-700">
          {DASHBOARD_LABELS.RESEND_REASON}
        </label>
        <Textarea
          id="resend-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          className="min-h-[80px]"
        />
      </div>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => handleModal({ isOpen: false })}>
          {DASHBOARD_LABELS.CANCEL}
        </Button>
        <Button onClick={handleResend} disabled={isLoading}>
          {DASHBOARD_LABELS.RESEND_CONFIRM}
        </Button>
      </div>
    </div>
  );
}
